import { useCallback } from 'react';
import React from 'react';

import { ItemAvailabilityStatus } from 'enums/menu';
import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { isOfferType } from 'state/order/utils';
import { maybeCreateCartEntriesFromOffer, maybeCreateCartEntryFromReward } from 'utils/cart';
import { routes } from 'utils/routing';

/**
 *
 * Returns a function to reorder a previous order.
 */
export const useHandleReorder = ({
  addToCart,
  navigate,
  linkTo,
  setPendingReorder,
  setReordering,
  storeHasSelection,
  // TODO: BKPE-1956 - unavailableCartEntries
  setUnavailableCartEntries,
  setReorderedOrderId,
}) => {
  const mapBackendToFrontend = useMapToFrontendCartEntry();
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const { sections: addOnSections } = useFeatureMenuAddOn();
  const hasAddons = addOnSections.length > 0;
  const enableCartV2 = useEnableCartV2Experiment();

  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering =
    useFlag <
      EnableMenuReorderingAvailabilityOverrideVariation >
      LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE || [];

  const { allRewards } = useLoyaltyRewardsList();
  const allRewardsMap = React.useMemo(() => {
    const dictionary = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);
  return useCallback(
    async (order, url = routes.orders) => {
      if (!order || !storeHasSelection) {
        // if there's no order or no order or store selection, navigate to where was initially intended
        return navigate(url);
      }
      setReordering(true);
      setReorderedOrderId(order.rbiOrderId);
      const { reorderCartEntries } = order.cart;
      const unavailableEntries = [];
      let containsExtras = false;

      const processEntry = async entry => {
        // Map backend entry to frontend
        let mappedEntries = [];
        // Offers or Rewards
        if (isOfferType(entry.type)) {
          const reward = allRewardsMap?.[entry.sanityId];
          if (reward) {
            mappedEntries = [maybeCreateCartEntryFromReward(entry, reward)];
          } else {
            // This function returns an array because there are some offers that could contain more than one item
            mappedEntries = maybeCreateCartEntriesFromOffer(entry, sanityIdsExcludedFromReordering);
          }
        } else {
          mappedEntries = [entry];
        }
        for (const entry of mappedEntries) {
          const { cartEntry: frontendEntry, status } = await mapBackendToFrontend(
            enableCartV2 ? { ...entry, quantity: 1 } : entry,
            reorderCartEntries
          );

          // If this is an extra we want to avoid adding it to the cart or unavailable items.
          if (status === ItemAvailabilityStatus.CART_EXTRA) {
            containsExtras = true;
            continue; // Skip to the next entry
          }

          // Check if the item is available
          if (status === ItemAvailabilityStatus.AVAILABLE) {
            addToCart(frontendEntry);
          } else {
            unavailableEntries.push(frontendEntry);
          }
        }
      };

      for (const entry of reorderCartEntries) {
        if (enableCartV2) {
          // Cart v2 process each item in the order individually.
          // ie: If the order has 3 items of the same item, we want to add 3 items to the cart and not 1 with quantity 3
          for (let i = 0; i < entry.quantity; i++) {
            await processEntry(entry);
          }
        } else {
          await processEntry(entry);
        }
      }

      setReordering(false);
      setReorderedOrderId(null);
      setPendingReorder(null);

      const hasUnavailableEntries = unavailableEntries.length > 0;

      // this is needed to prioritize the unavailable item modal
      if (hasUnavailableEntries) {
        setUnavailableCartEntries(unavailableEntries);
        navigate(url);
      } else if (enableAddExtras && hasAddons && containsExtras) {
        // If the order had extras go to the extras modal in the cart
        linkTo(routes.cartAddExtras);
      } else {
        navigate(routes.cart);
      }
    },
    [
      addToCart,
      enableAddExtras,
      hasAddons,
      linkTo,
      mapBackendToFrontend,
      navigate,
      setPendingReorder,
      setReorderedOrderId,
      setReordering,
      setUnavailableCartEntries,
      storeHasSelection,
      enableCartV2,
      allRewardsMap,
    ]
  );
};
